import OBR from '@owlbear-rodeo/sdk';
import type { PanelBounds, PanelGeometry } from './floatingPanel.ts';
import { clampPanelPosition, clampPanelSize, movePanelBy } from './floatingPanel.ts';

export const SHEET_PANEL_ID = 'com.lordofdisndat.dcc/floating-sheet';
export const SHEET_PANEL_CONTROL_CHANNEL = 'com.lordofdisndat.dcc/sheet-panel-control';
const PREF_KEY = 'com.lordofdisndat.dcc/sheet-panel/v1';

export interface SheetPanelPrefs extends PanelGeometry {
  preferFloating: boolean;
}

export const SHEET_PANEL_BOUNDS: PanelBounds = {
  minWidth: 360,
  maxWidth: 1000,
  minHeight: 420,
  maxHeight: 960,
};

const DEFAULT_PREFS: SheetPanelPrefs = {
  width: 420,
  height: 640,
  left: 28,
  top: 72,
  preferFloating: false,
};

export function readSheetPanelPrefs(): SheetPanelPrefs {
  try {
    const raw = globalThis.localStorage?.getItem(PREF_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const value = JSON.parse(raw) as Partial<SheetPanelPrefs>;
    const geometry = clampPanelSize({
      width: typeof value.width === 'number' ? value.width : DEFAULT_PREFS.width,
      height: typeof value.height === 'number' ? value.height : DEFAULT_PREFS.height,
      left: typeof value.left === 'number' ? value.left : DEFAULT_PREFS.left,
      top: typeof value.top === 'number' ? value.top : DEFAULT_PREFS.top,
    }, SHEET_PANEL_BOUNDS);
    return { ...geometry, preferFloating: value.preferFloating === true };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function writeSheetPanelPrefs(next: SheetPanelPrefs): SheetPanelPrefs {
  const value = { ...clampPanelSize(next, SHEET_PANEL_BOUNDS), preferFloating: next.preferFloating };
  try { globalThis.localStorage?.setItem(PREF_KEY, JSON.stringify(value)); } catch { /* convenience only */ }
  return value;
}

async function fitToViewport(next: SheetPanelPrefs): Promise<SheetPanelPrefs> {
  const [width, height] = await Promise.all([OBR.viewport.getWidth(), OBR.viewport.getHeight()]);
  return { ...clampPanelPosition(next, width, height), preferFloating: next.preferFloating };
}

export async function openFloatingSheetPanel(): Promise<SheetPanelPrefs> {
  const prefs = writeSheetPanelPrefs(await fitToViewport({ ...readSheetPanelPrefs(), preferFloating: true }));
  await OBR.popover.open({
    id: SHEET_PANEL_ID,
    url: '/?floating=1',
    width: prefs.width,
    height: prefs.height,
    anchorReference: 'POSITION',
    anchorPosition: { left: prefs.left, top: prefs.top },
    anchorOrigin: { horizontal: 'LEFT', vertical: 'TOP' },
    transformOrigin: { horizontal: 'LEFT', vertical: 'TOP' },
    disableClickAway: true,
  });
  return prefs;
}

export async function resizeSheetPanel(width: number, height: number, floating: boolean): Promise<SheetPanelPrefs> {
  const current = readSheetPanelPrefs();
  const next = writeSheetPanelPrefs({ ...clampPanelSize({ ...current, width, height }, SHEET_PANEL_BOUNDS), preferFloating: current.preferFloating });
  if (floating) {
    await Promise.all([
      OBR.popover.setWidth(SHEET_PANEL_ID, next.width),
      OBR.popover.setHeight(SHEET_PANEL_ID, next.height),
    ]);
  } else {
    await Promise.all([OBR.action.setWidth(next.width), OBR.action.setHeight(next.height)]);
  }
  return next;
}

export async function commitSheetPanelGeometry(nextGeometry: PanelGeometry, floating: boolean): Promise<SheetPanelPrefs> {
  const before = readSheetPanelPrefs();
  const fittedGeometry = await fitToViewport({ ...nextGeometry, preferFloating: before.preferFloating });
  const positionChanged = before.left !== fittedGeometry.left || before.top !== fittedGeometry.top;
  const next = writeSheetPanelPrefs({ ...fittedGeometry, preferFloating: before.preferFloating || positionChanged });

  if (positionChanged) {
    await OBR.broadcast.sendMessage(
      SHEET_PANEL_CONTROL_CHANNEL,
      { type: floating ? 'reopen-sheet-panel' : 'float-sheet-panel' },
      { destination: 'LOCAL' },
    );
  } else if (floating) {
    await Promise.all([
      OBR.popover.setWidth(SHEET_PANEL_ID, next.width),
      OBR.popover.setHeight(SHEET_PANEL_ID, next.height),
    ]);
  } else {
    await Promise.all([OBR.action.setWidth(next.width), OBR.action.setHeight(next.height)]);
  }
  return next;
}

/** Move on release. The ordinary action popover becomes a floating panel on its first move. */
export async function requestSheetPanelMoveBy(dx: number, dy: number, floating: boolean): Promise<SheetPanelPrefs> {
  const current = readSheetPanelPrefs();
  const moved = await fitToViewport({ ...movePanelBy(current, dx, dy), preferFloating: true });
  const next = writeSheetPanelPrefs(moved);
  await OBR.broadcast.sendMessage(
    SHEET_PANEL_CONTROL_CHANNEL,
    { type: floating ? 'reopen-sheet-panel' : 'float-sheet-panel' },
    { destination: 'LOCAL' },
  );
  return next;
}

export async function requestSheetPanelClose(floating: boolean): Promise<void> {
  if (!floating) {
    await OBR.action.close();
    return;
  }
  await OBR.broadcast.sendMessage(SHEET_PANEL_CONTROL_CHANNEL, { type: 'close-sheet-panel' }, { destination: 'LOCAL' });
}

export async function notifySheetPanelMounted(): Promise<void> {
  await OBR.broadcast.sendMessage(SHEET_PANEL_CONTROL_CHANNEL, { type: 'sheet-panel-mounted' }, { destination: 'LOCAL' });
}
